import {EditorView, basicSetup} from 'codemirror';
import {javascript} from '@codemirror/lang-javascript';
import {python} from '@codemirror/lang-python';
import {php} from '@codemirror/lang-php';
import {oneDark} from '@codemirror/theme-one-dark';
import {EditorState, Compartment} from '@codemirror/state';
import {keymap} from '@codemirror/view';
import {indentWithTab} from '@codemirror/commands';
import RFB from 'novnc-core';

export default (config) => ({
    selectedLanguage: Alpine.store('global').testConfig?.language || config.language || 'javascript',
    selectedLibrary: Alpine.store('global').testConfig?.library || config.library || 'playwright',

    editor: null,
    apiKey: config.apiKey || 'YOUR_API_KEY',
    apiUrl: config.apiUrl || '',
    vncAccess: config.vncAccess || 'password',
    vncPassword: config.vncPassword || '',
    languageCompartment: new Compartment(),
    themeCompartment: new Compartment(),
    languages: [
        {id: 'python', name: 'Python', libraries: ['playwright']},
        {id: 'javascript', name: 'JavaScript', libraries: ['playwright', 'puppeteer']},
        {id: 'php', name: 'PHP', libraries: ['chrome-php']}
    ],
    allLibraries: {
        'playwright': {id: 'playwright', name: 'Playwright'},
        'puppeteer': {id: 'puppeteer', name: 'Puppeteer'},
        'chrome-php': {id: 'chrome-php', name: 'Chrome-PHP'}
    },

    get libraries() {
        const lang = this.languages.find(l => l.id === this.selectedLanguage);
        if (!lang) return [];
        return lang.libraries.map(libId => this.allLibraries[libId]);
    },

    // Script execution state
    isRunning: false,
    sessionId: null,
    output: '',
    scriptStatus: null,
    pollingInterval: null,

    // Browser state
    browserSessionID: null,
    wssUrl: null,
    noVncUrl: null,
    browserLoading: false,
    browserRunning: false,
    browserConnectionFailed: false,
    rfb: null,
    connectionRetries: 0,
    maxRetries: 3,
    copiedWssUrl: false,

    // Resize state
    panelWidths: {code: 0},
    panelHeights: {browser: 0},
    isResizing: false,
    resizeType: null,
    resizeStartX: 0,
    resizeStartY: 0,
    resizeStartWidth: 0,
    resizeStartHeight: 0,

    init() {
        this.$nextTick(() => {
            this.initEditor();
            this.startBrowser();
            this.initPanelSizes();
        });

        // Watch for theme changes
        const observer = new MutationObserver(() => {
            this.updateEditorTheme();
        });
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme']
        });

        // Resize event listeners
        document.addEventListener('mousemove', (e) => this.handleResize(e));
        document.addEventListener('mouseup', () => this.stopResize());

        // Stop browser when user leaves the page (full page unload)
        this.beforeUnloadHandler = () => this.stopBrowserBeacon();
        window.addEventListener('beforeunload', this.beforeUnloadHandler);

        // Stop browser when user navigates away via alpine ajax
        this.ajaxBeforeHandler = (e) => {
            // only stop browser if navigating to a different page
            const target = e.target;
            if (target.tagName === 'A' && target.href && !target.href.includes('/test')) {
                this.stopBrowserBeacon();
            }
        };
        document.addEventListener('ajax:before', this.ajaxBeforeHandler);
    },

    initPanelSizes() {
        const container = this.$refs.mainContainer;
        if (!container) return;

        const containerWidth = container.offsetWidth;
        const containerHeight = container.offsetHeight;

        // Default: ~54% width for code panel (1094px on ~2016px screen)
        this.panelWidths.code = Math.floor(containerWidth * 0.54);
        // Default: ~78% height for browser panel (698px on ~895px container)
        this.panelHeights.browser = Math.floor(containerHeight * 0.78);
    },

    startHorizontalResize(e) {
        this.isResizing = true;
        this.resizeType = 'horizontal';
        this.resizeStartX = e.clientX;
        this.resizeStartWidth = this.panelWidths.code;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    },

    startVerticalResize(e) {
        this.isResizing = true;
        this.resizeType = 'vertical';
        this.resizeStartY = e.clientY;
        this.resizeStartHeight = this.panelHeights.browser;
        document.body.style.cursor = 'row-resize';
        document.body.style.userSelect = 'none';
    },

    handleResize(e) {
        if (!this.isResizing) return;

        if (this.resizeType === 'horizontal') {
            const delta = e.clientX - this.resizeStartX;
            const newWidth = this.resizeStartWidth + delta;
            const container = this.$refs.mainContainer;
            const maxWidth = container.offsetWidth - 304; // 300 min right + 4 handle
            this.panelWidths.code = Math.max(300, Math.min(newWidth, maxWidth));
        } else if (this.resizeType === 'vertical') {
            const delta = e.clientY - this.resizeStartY;
            const newHeight = this.resizeStartHeight + delta;
            const container = this.$refs.mainContainer;
            const maxHeight = container.offsetHeight - 144; // 140 min output + 4 handle
            this.panelHeights.browser = Math.max(200, Math.min(newHeight, maxHeight));
        }

        // Trigger noVNC to rescale during resize
        if (this.rfb) {
            this.rfb.scaleViewport = true;
        }
    },

    stopResize() {
        if (!this.isResizing) return;
        this.isResizing = false;
        this.resizeType = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';

        // Trigger noVNC to rescale after resize
        if (this.rfb) {
            requestAnimationFrame(() => {
                this.rfb.scaleViewport = true;
            });
        }
    },

    initEditor() {
        const container = document.getElementById('codemirror-editor');
        if (!container) return;

        const isDark = this.isDarkMode();

        this.editor = new EditorView({
            state: EditorState.create({
                doc: '// Loading...',
                extensions: [
                    basicSetup,
                    keymap.of([indentWithTab]),
                    EditorState.tabSize.of(4),
                    this.languageCompartment.of(this.getLanguageExtension()),
                    this.themeCompartment.of(isDark ? oneDark : []),
                    EditorView.theme({
                        '&': {height: '100%'},
                        '.cm-scroller': {overflow: 'auto'}
                    })
                ]
            }),
            parent: container
        });
    },

    isDarkMode() {
        const theme = document.documentElement.dataset.theme;
        return theme === 'business' || theme === 'dark';
    },

    updateEditorTheme() {
        if (!this.editor) return;
        const isDark = this.isDarkMode();
        this.editor.dispatch({
            effects: this.themeCompartment.reconfigure(isDark ? oneDark : [])
        });
    },

    getLanguageExtension() {
        switch (this.selectedLanguage) {
            case 'python':
                return python();
            case 'php':
                return php();
            default:
                return javascript();
        }
    },

    updateEditorLanguage() {
        if (!this.editor) return;
        this.editor.dispatch({
            effects: this.languageCompartment.reconfigure(this.getLanguageExtension())
        });
    },

    getFileExtension() {
        const extMap = {
            'javascript': 'js',
            'python': 'py',
            'php': 'txt'
        };
        return extMap[this.selectedLanguage] || 'txt';
    },

    async loadExample() {
        const ext = this.getFileExtension();
        const filename = `${this.selectedLanguage}-${this.selectedLibrary}.${ext}`;

        try {
            const response = await fetch(`/examples/${filename}`);
            if (!response.ok) {
                throw new Error('Example not found');
            }
            let code = await response.text();

            // Replace placeholders with actual values
            if (this.wssUrl) {
                code = code.replace(/\{\{WSS_URL\}\}/g, this.wssUrl);
            }
            code = code.replace(/\{\{API_KEY\}\}/g, this.apiKey);

            if (this.editor) {
                this.editor.dispatch({
                    changes: {
                        from: 0,
                        to: this.editor.state.doc.length,
                        insert: code
                    }
                });
            }
        } catch (error) {
            console.error('Error loading example:', error);
            if (this.editor) {
                this.editor.dispatch({
                    changes: {
                        from: 0,
                        to: this.editor.state.doc.length,
                        insert: `// Example not available for ${this.selectedLanguage} + ${this.selectedLibrary}`
                    }
                });
            }
        }
    },

    selectLanguage(langId) {
        this.selectedLanguage = langId;
        // Select first available library for this language
        const lang = this.languages.find(l => l.id === langId);
        if (lang && lang.libraries.length > 0) {
            this.selectedLibrary = lang.libraries[0];
        }
        this.updateEditorLanguage();
        this.loadExample();
    },

    selectLibrary(libId) {
        this.selectedLibrary = libId;
        this.loadExample();
    },

    async runScript() {
        if (!this.editor) return;

        const code = this.editor.state.doc.toString();
        if (!code.trim()) {
            this.output = '> Error: No code to run';
            return;
        }

        this.isRunning = true;
        this.output = '';
        this.scriptStatus = 'running';

        try {
            const response = await fetch('/script/run', {
                method: 'POST',
                headers: {'Content-Type': 'application/x-www-form-urlencoded'},
                body: new URLSearchParams({
                    code: code,
                    language: this.selectedLanguage,
                    library: this.selectedLibrary
                })
            });

            const data = await response.json();

            if (!data.success) {
                this.output = '> Error: ' + (data.message || 'Failed to start script');
                this.isRunning = false;
                this.scriptStatus = 'failed';
                return;
            }

            this.sessionId = data.sessionId;
            this.output = '';
            this.startPolling();
        } catch (error) {
            this.output = '> Error: ' + error.message;
            this.isRunning = false;
            this.scriptStatus = 'failed';
        }
    },

    startPolling() {
        this.stopPolling();
        this.pollingInterval = setInterval(() => this.pollStatus(), 1000);
    },

    stopPolling() {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
    },

    async pollStatus() {
        if (!this.sessionId) return;

        try {
            const response = await fetch('/script/status?sessionId=' + this.sessionId);
            const data = await response.json();

            if (!data.success) {
                this.output = '> Error: ' + (data.message || 'Failed to get status');
                this.stopPolling();
                this.isRunning = false;
                this.scriptStatus = 'failed';
                return;
            }

            // Only update output if there's actual content
            if (data.output && data.output.trim()) {
                this.output = data.output;
            }
            this.scriptStatus = data.status;

            if (data.status !== 'running') {
                this.stopPolling();
                this.isRunning = false;
            }
        } catch (error) {
            this.output = '> Error polling status: ' + error.message;
            this.stopPolling();
            this.isRunning = false;
            this.scriptStatus = 'failed';
        }
    },

    async stopScript() {
        if (!this.sessionId) return;

        // Stop polling first to prevent race conditions
        this.stopPolling();
        this.isRunning = false;

        this.output += '\n> Stopping script...';

        try {
            const response = await fetch('/script/stop?sessionId=' + this.sessionId, {
                method: 'POST'
            });
            const data = await response.json();

            if (data.success) {
                this.output += '\n> Script stopped';
                this.scriptStatus = 'stopped';
            } else {
                this.output += '\n> Error stopping: ' + (data.message || 'Unknown error');
            }
        } catch (error) {
            this.output += '\n> Error: ' + error.message;
        }
    },

    async startBrowser() {
        this.browserLoading = true;
        this.browserConnectionFailed = false;

        try {
            const response = await fetch('https://' + this.apiUrl + '/browser/start', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'x-cdt-api-key': this.apiKey
                },
                body: new URLSearchParams({disconnectTimeoutSeconds: '3600'})
            });

            const json = await response.json();

            if (json.success) {
                this.browserSessionID = json.sessionID;
                this.wssUrl = json.wssUrl;
                this.noVncUrl = json.noVncUrl;
                this.loadExample(); // reload example with new wssUrl
                this.connectVnc();
            } else {
                this.browserLoading = false;
                this.browserConnectionFailed = true;
                Alpine.store('toast').show(json.message || 'Failed to start browser', 'error');
            }
        } catch (e) {
            this.browserLoading = false;
            this.browserConnectionFailed = true;
            console.error(e);
            Alpine.store('toast').show('Failed to start browser', 'error');
        }
    },

    connectVnc() {
        if (!this.noVncUrl || !this.$refs.vncCanvas) {
            console.error('Missing noVncUrl or element');
            this.browserLoading = false;
            return;
        }

        const wsUrl = this.noVncUrl.replace('https://', 'wss://') + '/websockify';
        console.log('Connecting to VNC:', wsUrl);

        this.rfb = new RFB(this.$refs.vncCanvas, wsUrl, {
            credentials: {password: this.vncPassword},
            scaleViewport: true,
            resizeSession: false
        });

        this.rfb.viewOnly = false;
        this.rfb.clipViewport = true;
        this.rfb.qualityLevel = 0;
        this.rfb.compressionLevel = 9;

        this.rfb.addEventListener('connect', () => {
            console.log('VNC connected');
            this.connectionRetries = 0;
            this.browserLoading = false;
            this.browserRunning = true;

            setTimeout(() => {
                if (this.rfb) this.rfb.scaleViewport = true;
            }, 100);
        });

        this.rfb.addEventListener('disconnect', (e) => {
            console.log('VNC disconnected', e.detail);

            if (this.connectionRetries < this.maxRetries) {
                this.connectionRetries++;
                console.log(`Retrying connection (${this.connectionRetries}/${this.maxRetries}) in 1 second...`);
                setTimeout(() => this.connectVnc(), 1000);
            } else {
                this.browserLoading = false;
                this.browserConnectionFailed = true;
            }
        });
    },

    async stopBrowser() {
        if (!this.browserSessionID) return;

        try {
            await fetch('https://' + this.apiUrl + '/browser/stop', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'x-cdt-api-key': this.apiKey
                },
                body: new URLSearchParams({sessionID: this.browserSessionID})
            });
        } catch (e) {
            console.error('Error stopping browser:', e);
        }

        if (this.rfb) {
            this.rfb.disconnect();
            this.rfb = null;
        }

        this.browserSessionID = null;
        this.wssUrl = null;
        this.noVncUrl = null;
        this.browserRunning = false;
    },

    // stop browser using beacon api when user leaves the page
    // sendBeacon is more reliable than fetch during page unload
    stopBrowserBeacon() {
        if (!this.browserSessionID) return;

        // save to global store so dashboard can filter it out
        Alpine.store('global').stoppingSessionID = this.browserSessionID;

        const url = 'https://' + this.apiUrl + '/browser/stop';
        const data = new URLSearchParams({
            sessionID: this.browserSessionID,
            apiKey: this.apiKey
        });

        navigator.sendBeacon(url, data);
    },

    destroy() {
        this.stopPolling();
        this.stopBrowser();
        if (this.editor) {
            this.editor.destroy();
            this.editor = null;
        }
        // cleanup event listeners
        if (this.beforeUnloadHandler) {
            window.removeEventListener('beforeunload', this.beforeUnloadHandler);
        }
        if (this.ajaxBeforeHandler) {
            document.removeEventListener('ajax:before', this.ajaxBeforeHandler);
        }
    }
})
